import { NgStyle } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { SetFormAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { GetProfile } from '@app/finxone-web-frontend/app/lib/actions/user-profile.action';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { KeycloakWrapperService } from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { RoleState, RoleStateModel } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { setActiveRoleIfNeeded } from '@app/finxone-web-frontend/app/lib/utils/quick-link-page-url.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  BaseCssProperties,
  ConditionEnum,
  ConditionalWidgetVariableEnum,
  DummyRole,
  ProfileStateModel,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, map, takeUntil } from 'rxjs';
import { SafeHtmlPipe } from '@app/finxone-web-frontend/app/lib/pipes/SafeHtml.pipe';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { BaseComponent } from '../base/base.component';
@Component({
  selector: 'finxone-web-frontend-conditional-widget',
  templateUrl: './conditional-widget.component.html',
  styleUrls: ['./conditional-widget.component.scss'],
  imports: [NgStyle, MatIconModule, SafeHtmlPipe],
})
export class ConditionalWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public conditionalWidgetData: {
    title: string;
    url: string | undefined;
    role: string | undefined;
    zone: string | undefined;
    externalLink: string | undefined;
    browserBehaviour: string | undefined;
  } = {
    title: '',
    url: undefined,
    role: '',
    zone: '',
    externalLink: undefined,
    browserBehaviour: undefined,
  };
  textContentStyle: baseCssConfigStyle;
  private quickLinkZones = [APP_ZONES.ONBOARDING, APP_ZONES.VERIFICATION, APP_ZONES.ORGANISATION_ONBOARDING];
  public headerSubheaderTemplate: string;
  public rightIcon: string;
  public leftIcon: string;
  @Select(RoleState.getRole)
  private roles!: Observable<RoleStateModel>;

  public profileDetail!: ProfileStateModel;

  roleInfo: RoleStateModel;

  ifConditionAttri: UiZoneWidgetAttributeConfig;
  elseConditionAttri: UiZoneWidgetAttributeConfig;
  rightIconSize: { width: string; height: string };
  leftIconSize: { width: string; height: string };

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    public pageConfiguration: PageConfigurationProvider,
    public store: Store,
    private _router: Router,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private keycloakService: KeycloakWrapperService,
    private iconCacheService: IconCacheService,
  ) {
    super();
    // Not a great fix but temp to make sure the buttons update when the verification zone status page gets loaded
    this.store.dispatch(new GetProfile());
  }

  ngOnInit() {
    this.onGetAttributes();
    this.onLoadWidgetConfiguration();
  }

  private onGetAttributes() {
    if (this.dataIn != undefined && this.dataIn != null) {
      this.ifConditionAttri = this.dataIn.widgetConfig?.nestedWidgets?.[
        'ifCondition'
      ] as UiZoneWidgetAttributeConfig;
      this.elseConditionAttri = this.dataIn.widgetConfig?.nestedWidgets?.[
        'elseCondition'
      ] as UiZoneWidgetAttributeConfig;

      this.profileDetail = this.store.selectSnapshot(ProfileState.getProfile);

      switch (this.dataIn.attri?.['selectedVariable']) {
        case ConditionalWidgetVariableEnum.KYC_PERSONAL:
          this.checkConditionWithAttribute('kycVerificationStatus', 'selectedValue');
          break;

        case ConditionalWidgetVariableEnum.KYB_BUSINESS:
          this.checkConditionWithAttribute('activeOrganisationKybStatus', 'selectedValue');
          break;

        case ConditionalWidgetVariableEnum.USER_LOGGED_IN:
          this.checkLoggedInStatus();
          break;

        default:
          this.checkConditionWithAttribute('orgCounter', 'businessSelectedValue');
          break;
      }

      if (this.attri != null) {
        this.setPageUrl();
        this.conditionalWidgetData.externalLink = this.attri?.['externalUrl'] ?? null;
        this.conditionalWidgetData.browserBehaviour = this.attri?.['browserBehaviour'] ?? null;
      }
    }
  }

  public checkLoggedInStatus() {
    const isLoggedIn = this.keycloakService.isLoggedIn().toString();
    const selectedValue = this.dataIn.attri?.['selectedValue'];

    const conditionMap = {
      [ConditionEnum.EQUAL]: (a: string, b: string) => a === b,
      [ConditionEnum.NOT_EQUAL]: (a: string, b: string) => a !== b,
    };

    const selectedCondition = this.dataIn.attri?.['selectedCondition'] as '===' | '!==';

    if (conditionMap?.[selectedCondition]?.(isLoggedIn, selectedValue)) {
      this.attri = this.ifConditionAttri;
    } else {
      this.attri = this.elseConditionAttri;
    }
  }

  public checkConditionWithAttribute(profileStateKey: keyof ProfileStateModel, valueKey: string) {
    const profileValue = (this.profileDetail?.[profileStateKey] as string) || '0';
    const selectedValue = this.dataIn.attri?.[valueKey];

    const conditionMap = {
      [ConditionEnum.EQUAL]: (a: string, b: string) => a === b,
      [ConditionEnum.NOT_EQUAL]: (a: string, b: string) => a !== b,
      [ConditionEnum.LESS_THAN]: (a: string, b: string) => a < b,
      [ConditionEnum.LESS_THAN_EQUAL]: (a: string, b: string) => a <= b,
      [ConditionEnum.GREATER_THAN]: (a: string, b: string) => a > (b ?? 0),
      [ConditionEnum.GREATER_THAN_EQUAL]: (a: string, b: string) => a >= b,
    };

    const selectedCondition = this.dataIn.attri?.['selectedCondition'] as ConditionEnum;

    if (conditionMap?.[selectedCondition]?.(profileValue, selectedValue)) {
      this.attri = this.ifConditionAttri;
    } else {
      this.attri = this.elseConditionAttri;
    }
  }

  private onLoadWidgetConfiguration(): void {
    this.leftIcon = this.attri?.['widgetProperties']?.['lefticon'] as string;
    this.rightIcon = this.attri?.['widgetProperties']?.['righticon'] as string;

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BORDER,
      BaseCssProperties.COLOR,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.V_ALIGN,
    ]);

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.V_ALIGN_DISPLAY_BLOCK,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);

    this.headerSubheaderTemplate = this.attri?.widgetProperties?.textContent || '';
    const template = Handlebars.compile(this.headerSubheaderTemplate);
    buildHandleBarModel(
      this.store,
      this.headerSubheaderTemplate,
      this.countriesCurrenciesService,
      this.iconCacheService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.headerSubheaderTemplate = filterIfHandlebarNotRendered(template(model));
      });

    this.rightIconSize = {
      width:
        (this.attri?.css_config?.['righticonSize'] ?? 30) +
        (this.attri?.css_config?.['rightIconUnit'] ?? 'px'),
      height:
        (this.attri?.css_config?.['righticonSize'] ?? 30) +
        (this.attri?.css_config?.['rightIconUnit'] ?? 'px'),
    };

    this.leftIconSize = {
      width:
        (this.attri?.css_config?.['lefticonSize'] ?? 30) + (this.attri?.css_config?.['leftIconUnit'] ?? 'px'),
      height:
        (this.attri?.css_config?.['lefticonSize'] ?? 30) + (this.attri?.css_config?.['leftIconUnit'] ?? 'px'),
    };
  }

  public setPageUrl() {
    let pageUrl: string | undefined;
    this.checkForDummyRole();

    if (this.attri['pageUrl']) {
      pageUrl = this.attri['pageUrl'];
    }

    if (
      this.quickLinkZones.includes(this.attri?.['zone'] as APP_ZONES) &&
      !pageUrl?.includes('sumsub-workflow')
    ) {
      this.conditionalWidgetData.url = `/zones/${formatNameAsUrl(this.attri?.['zone'])}/${pageUrl}/${
        this.attri?.['role']
      }/default`;
    } else if (pageUrl) {
      this.conditionalWidgetData.url = `/zones/${formatNameAsUrl(this.attri?.['zone'])}/${pageUrl}`;
    } else {
      this.conditionalWidgetData.url = `/zones/${formatNameAsUrl(this.attri?.['zone'])}`;
    }
    this.conditionalWidgetData.role = this.attri?.['role'];
    this.conditionalWidgetData.zone = this.attri?.['zone'];
  }

  public checkForDummyRole() {
    if (this.attri?.['role'] === DummyRole.EMPTY_ROLE) {
      this.roles
        .pipe(
          takeUntil(this.destroy$),
          map((roleInfo) => {
            this.attri['role'] = roleInfo.role;
            this.roleInfo = {
              ...this.roleInfo,
              role: roleInfo.role,
            } as RoleStateModel;
          }),
        )
        .subscribe();
    }
  }

  public goToLink() {
    if (this.conditionalWidgetData.browserBehaviour && this.conditionalWidgetData.externalLink) {
      if (this.conditionalWidgetData.browserBehaviour === 'new-tab') {
        window.open(this.conditionalWidgetData.externalLink, '_blank');
      } else if (this.conditionalWidgetData.browserBehaviour === 'new-window') {
        window.open(this.conditionalWidgetData.externalLink, '_blank', 'width=800, height=600');
      } else {
        window.open(this.conditionalWidgetData.externalLink, '_self');
      }
      return;
    }
    const isSameRole = this.roleInfo?.role === this.conditionalWidgetData.role;
    const isOnboardingOrVerification =
      this.conditionalWidgetData.zone === APP_ZONES.ONBOARDING ||
      this.conditionalWidgetData.zone === APP_ZONES.VERIFICATION ||
      this.conditionalWidgetData.zone === APP_ZONES.ORGANISATION_ONBOARDING ||
      this.conditionalWidgetData.zone === APP_ZONES.ORGANISATION_VERIFICATION;

    if (this.attri['navigationOption'] !== 'use-as-blank') {
      this.navigationEvent(isSameRole, isOnboardingOrVerification);
      setActiveRoleIfNeeded(this.conditionalWidgetData.zone, this.store);
      this.onSetUserIdIntoFormState();
    }
  }

  private navigationEvent(isSameRole: boolean, isOnboardingOrVerification: boolean) {
    if (this.conditionalWidgetData.url?.includes('sumsub-workflow')) {
      this._router.navigateByUrl(this.conditionalWidgetData.url ?? '');
    } else if (
      (isSameRole && this.conditionalWidgetData.url) ||
      isOnboardingOrVerification ||
      this.conditionalWidgetData.role === 'no-role'
    ) {
      this._router.navigate([this.conditionalWidgetData.url]);
    } else if (this.conditionalWidgetData.zone === APP_ZONES.VERIFICATION) {
      this._router.navigateByUrl(this.conditionalWidgetData.url ?? '');
    } else {
      this._router.navigateByUrl(this.conditionalWidgetData.url ?? '');
    }
  }

  public onSetUserIdIntoFormState() {
    const profileStateValue = this.store.selectSnapshot(ProfileState.getProfile);
    this.store.dispatch(
      new SetFormAction({
        type: '',
        formData: { userId: profileStateValue?.id ?? '' },
      }),
    );
  }
}
